import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  createAdminCounsellor,
  deleteAdminCounsellor,
  fetchAdminCounsellor,
  fetchAdminCounsellorStats,
  fetchAdminCounsellors,
  updateAdminCounsellor,
} from "../../../services/adminApi";
import { useToast } from "../../../hook/useToast";
import AdminDashboardLayout from "../AdminDashboardLayout";
import StatsCard from "../StatsCard";
import AddCounsellorModal from "./AddCounsellorModal";

const DEFAULT_PAGE_SIZE = 20;
const NAVY = "#292E5C";
const ORANGE = "#FFA54D";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

const COLUMNS = [
  { id: "sr", label: "Sr No.", width: 78, align: "left" },
  { id: "name", label: "Counsellor name", width: 260, align: "left" },
  { id: "email", label: "Mail Id", width: 260, align: "left" },
  { id: "years_of_experience", label: "Year of Experience", width: 166, align: "center" },
  { id: "linkedin", label: "LinkedIn", width: 126, align: "center" },
  { id: "sessions_completed", label: "Session Completed", width: 178, align: "center" },
  { id: "retainers", label: "Retainers", width: 132, align: "center" },
  { id: "amount", label: "Amount Generated", width: 178, align: "center" },
  { id: "actions", label: "Action", width: 118, align: "center" },
];

const headerTextSx = {
  color: "#667085",
  fontSize: 12,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  lineHeight: "18px",
  whiteSpace: "nowrap",
};

const bodyTextSx = {
  color: "#344054",
  fontSize: 13,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
  lineHeight: "18px",
};

const selectSx = {
  width: 155,
  height: 36,
  borderRadius: "7px",
  bgcolor: "#fff",
  color: "#888888",
  fontSize: 12,
  fontFamily: "Montserrat, sans-serif",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DCDFDE" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#DCDFDE" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CEDA" },
  "& .MuiSelect-select": {
    py: 0,
    pl: 1.25,
    pr: 3,
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
};

const searchSx = {
  width: 245,
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "7px",
    bgcolor: "#fff",
    fontSize: 12,
    fontFamily: "Montserrat, sans-serif",
    "& fieldset": { borderColor: "#E2ECF3" },
    "&:hover fieldset": { borderColor: "#E2ECF3" },
    "&.Mui-focused fieldset": { borderColor: "#C8CEDA" },
  },
  "& .MuiInputBase-input": {
    color: "#292948",
    "&::placeholder": {
      color: "#888888",
      opacity: 1,
    },
  },
};

const tableCellSx = {
  height: 72,
  px: 1.25,
  py: 2,
  borderBottom: "1px solid #EAECF0",
};

const formatNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "--";
  }

  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString("en-IN");
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") {
    return "--";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  return `\u20B9${number.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const getInitials = (name = "") => {
  const initials = String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return initials.toUpperCase() || "C";
};

const normalizeLinkedinUrl = (value) => {
  const url = String(value || "").trim();

  if (!url) {
    return "";
  }

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const normalizeMediaUrl = (value) => {
  const url = String(value || "").trim();

  if (!url || /^(https?:|blob:|data:)/i.test(url)) {
    return url;
  }

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const pickFirstValue = (record, keys, fallback = "") => {
  for (const key of keys) {
    const value = record?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const getPaginationItems = (page, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  if (start > 2) {
    items.push("dots-start");
  }

  for (let index = start; index <= end; index += 1) {
    items.push(index);
  }

  if (end < totalPages - 1) {
    items.push("dots-end");
  }

  items.push(totalPages);
  return items;
};

const extractApiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  const errors = responseData?.errors || responseData?.data || responseData;

  if (typeof errors === "string" && errors.trim()) {
    return errors;
  }

  if (errors && typeof errors === "object") {
    const firstValue = Object.values(errors).flat().find(Boolean);

    if (typeof firstValue === "string") {
      return firstValue;
    }
  }

  return fallback;
};

const CounsellorsPage = ({
  fetchStats = fetchAdminCounsellorStats,
  fetchItems = fetchAdminCounsellors,
  createItem = createAdminCounsellor,
  fetchItem = fetchAdminCounsellor,
  updateItem = updateAdminCounsellor,
  deleteItem = deleteAdminCounsellor,
  onViewItem,
  layoutComponent: LayoutComponent = AdminDashboardLayout,
  label = "Counsellor",
}) => {
  const { showToast } = useToast();
  const [stats, setStats] = useState(null);
  const [counsellors, setCounsellors] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);
  const [counsellorsLoading, setCounsellorsLoading] = useState(false);
  const [counsellorsError, setCounsellorsError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCounsellor, setEditCounsellor] = useState(null);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await fetchStats();
      setStats(response?.data?.data || {});
    } catch {
      showToast("Failed to load counsellor stats", "error");
    } finally {
      setStatsLoading(false);
    }
  }, [fetchStats, showToast]);

  const loadCounsellors = useCallback(async () => {
    setCounsellorsLoading(true);
    setCounsellorsError("");

    try {
      const response = await fetchItems({
        page,
        pageSize,
        search: debouncedSearch,
      });
      const data = response?.data?.data || {};

      setCounsellors(Array.isArray(data.counsellors) ? data.counsellors : []);
      setPagination({
        page: data.pagination?.page || page,
        page_size: data.pagination?.page_size || pageSize,
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 0,
      });
    } catch {
      setCounsellors([]);
      setCounsellorsError("Unable to load counsellors right now.");
      showToast("Failed to load counsellors", "error");
    } finally {
      setCounsellorsLoading(false);
    }
  }, [debouncedSearch, fetchItems, page, pageSize, showToast]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadCounsellors();
  }, [loadCounsellors]);

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const totalRecords = pagination.total_records || 0;
  const paginationItems = useMemo(
    () => getPaginationItems(page, totalPages),
    [page, totalPages]
  );

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleFilterClick = () => {
    showToast(`${label} filters are not available yet.`, "info");
  };

  const handleCreateCounsellor = async (payload) => {
    setCreating(true);

    try {
      await createItem(payload);
      showToast(`${label} added successfully`, "success");
      await loadStats();

      if (page === 1 && debouncedSearch === "") {
        await loadCounsellors();
      } else {
        setSearch("");
        setDebouncedSearch("");
        setPage(1);
      }

      return { ok: true };
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to add counsellor.");
      showToast(message, "error");
      return { ok: false, message };
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEditCounsellor = async (counsellor) => {
    const counsellorId = counsellor?.counsellor_id;

    if (!counsellorId) {
      return;
    }

    setEditLoadingId(counsellorId);

    try {
      const response = await fetchItem(counsellorId);
      setEditCounsellor(response?.data?.data || counsellor);
      setEditModalOpen(true);
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        "Failed to load counsellor details."
      );
      showToast(message, "error");
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleUpdateCounsellor = async (payload) => {
    const counsellorId = editCounsellor?.counsellor_id;

    if (!counsellorId) {
      return { ok: false, message: `${label} details are not loaded.` };
    }

    setUpdating(true);

    try {
      await updateItem(counsellorId, payload);
      showToast(`${label} updated successfully`, "success");
      await loadStats();
      await loadCounsellors();
      return { ok: true };
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        "Failed to update counsellor."
      );
      showToast(message, "error");
      return { ok: false, message };
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseEditCounsellor = () => {
    if (!updating) {
      setEditModalOpen(false);
      setEditCounsellor(null);
    }
  };

  const handleDeleteCounsellor = async () => {
    const counsellorId = deleteTarget?.id;

    if (!counsellorId) {
      return;
    }

    setDeletingId(counsellorId);

    try {
      await deleteItem(counsellorId);
      showToast(`${label} deleted successfully`, "success");
      setDeleteTarget(null);
      await loadStats();

      if (counsellors.length === 1 && page > 1) {
        setPage((current) => Math.max(1, current - 1));
      } else {
        await loadCounsellors();
      }
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to delete counsellor.");
      showToast(message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <LayoutComponent>
      <Box>
        <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#171725", mb: 1.4 }}>
          {label}s
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 2.2,
          }}
        >
          <StatsCard
            value={statsLoading ? "--" : formatNumber(stats?.total_counsellors)}
            label={`Total ${label}s`}
            icon="total"
            bgColor="#F5F6FA"
          />
          <StatsCard
            value={statsLoading ? "--" : formatNumber(stats?.counsellors_this_month)}
            label={`${label}s This Month`}
            icon="month"
            bgColor="#FFF9F1"
          />
          <StatsCard
            value={statsLoading ? "--" : formatNumber(stats?.counsellors_this_year)}
            label={`${label}s This Year`}
            icon="year"
            bgColor="#F2F6FF"
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: "10px",
            bgcolor: "rgba(239, 239, 239, 0.40)",
            borderRadius: "7px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", lg: "center" },
              gap: 1,
              flexDirection: { xs: "column", lg: "row" },
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontSize: 18,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 500,
                lineHeight: "24px",
              }}
            >
              {label}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: "5px",
                flexWrap: "wrap",
              }}
            >
              <Select
                displayEmpty
                value={pageSize}
                onChange={handlePageSizeChange}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(value) => `${value} Records`}
                sx={selectSx}
              >
                <MenuItem value={10}>10 Records</MenuItem>
                <MenuItem value={20}>20 Records</MenuItem>
                <MenuItem value={50}>50 Records</MenuItem>
                <MenuItem value={100}>100 Records</MenuItem>
              </Select>

              <TextField
                placeholder="Search..."
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={searchSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ fontSize: 16, color: "#585874" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="button"
                variant="outlined"
                startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
                onClick={handleFilterClick}
                sx={{
                  height: 36,
                  px: 1.25,
                  borderRadius: "7px",
                  borderColor: "#E2ECF3",
                  bgcolor: "#fff",
                  color: "#363636",
                  textTransform: "none",
                  fontSize: 12,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                  lineHeight: "15px",
                  "&:hover": {
                    borderColor: "#D7DAE5",
                    bgcolor: "#fff",
                  },
                }}
              >
                Filter
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={() => setAddModalOpen(true)}
                sx={{
                  height: 36,
                  px: 1.5,
                  borderRadius: "5px",
                  bgcolor: ORANGE,
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  lineHeight: "14px",
                  boxShadow:
                    "0px 1px 2px rgba(16, 24, 40, 0.04), 0px 1px 2px rgba(16, 24, 40, 0.04)",
                  "&:hover": {
                    bgcolor: "#F59A3D",
                    boxShadow:
                      "0px 1px 2px rgba(16, 24, 40, 0.04), 0px 1px 2px rgba(16, 24, 40, 0.04)",
                  },
                }}
              >
                Add a {label}
              </Button>
            </Box>
          </Box>

          {counsellorsError ? (
            <Alert severity="error" sx={{ borderRadius: "7px" }}>
              {counsellorsError}
            </Alert>
          ) : null}

          <TableContainer
            sx={{
              position: "relative",
              minHeight: 420,
              bgcolor: "#fff",
              borderRadius: "7px",
              border: "1px solid #DCDFDE",
              overflow: "auto",
            }}
          >
            {counsellorsLoading ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.72)",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={30} sx={{ color: NAVY }} />
              </Box>
            ) : null}

            <Table sx={{ minWidth: 1510 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F9FAFB", height: 44 }}>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        width: column.width,
                        minWidth: column.width,
                        height: 44,
                        px: 1.25,
                        py: 1.5,
                        borderBottom: "1px solid #EAECF0",
                      }}
                    >
                      <Typography sx={headerTextSx}>{column.id === "name" ? `${label} name` : column.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {!counsellorsLoading && counsellors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 7 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#171725" }}>
                        No counsellors found
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.6 }}>
                        Add a counsellor to show them in this table.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  counsellors.map((counsellor, index) => {
                    const serialNumber = (page - 1) * pageSize + index + 1;
                    const counsellorName = counsellor.name || `Unnamed ${label}`;
                    const photoUrl = normalizeMediaUrl(counsellor.photo);
                    const linkedinUrl = normalizeLinkedinUrl(counsellor.linkedin);
                    const retainers = pickFirstValue(
                      counsellor,
                      ["retainers", "retainer_count", "total_retainers", "students_retained"],
                      0
                    );
                    const amountGenerated = pickFirstValue(
                      counsellor,
                      ["retainers_amount_generated", "amount_generated", "total_amount_generated"],
                      0
                    );

                    return (
                      <TableRow
                        key={counsellor.counsellor_id || serialNumber}
                        hover
                        sx={{
                          height: 72,
                          "&:hover": { bgcolor: "#FBFCFF" },
                        }}
                      >
                        <TableCell sx={tableCellSx}>
                          <Typography
                            sx={{
                              color: "#101828",
                              fontSize: 13,
                              fontFamily: "Poppins, sans-serif",
                              fontWeight: 400,
                              lineHeight: "20px",
                            }}
                          >
                            {serialNumber}
                          </Typography>
                        </TableCell>

                        <TableCell sx={tableCellSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                              src={photoUrl || undefined}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "#C4C4C4",
                                color: NAVY,
                                border: "1px solid #F3F3F3",
                                fontSize: 13,
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 700,
                              }}
                            >
                              {!photoUrl ? getInitials(counsellorName) : null}
                            </Avatar>

                            <Typography
                              sx={{
                                color: "#101828",
                                fontSize: 13,
                                fontFamily: "Montserrat, sans-serif",
                                fontWeight: 500,
                                lineHeight: "20px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 180,
                              }}
                            >
                              {counsellorName}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={tableCellSx}>
                          <Typography
                            title={counsellor.email || ""}
                            sx={{
                              ...bodyTextSx,
                              maxWidth: 232,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {counsellor.email || "--"}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>
                            {formatNumber(counsellor.years_of_experience)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <IconButton
                            component={linkedinUrl ? "a" : "button"}
                            href={linkedinUrl || undefined}
                            target={linkedinUrl ? "_blank" : undefined}
                            rel={linkedinUrl ? "noreferrer" : undefined}
                            disabled={!linkedinUrl}
                            aria-label={
                              linkedinUrl
                                ? `Open ${counsellorName} LinkedIn profile`
                                : `${counsellorName} LinkedIn profile not available`
                            }
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "7px",
                              color: linkedinUrl ? "#0149FF" : "#B5B5B5",
                              cursor: linkedinUrl ? "pointer" : "default",

                              "&:hover": {
                                bgcolor: linkedinUrl ? "#EEF4FF" : "transparent",
                              },

                              "&.Mui-disabled": {
                                color: "#B5B5B5",
                                pointerEvents: "auto",
                              },
                            }}
                          >
                            <LinkIcon
                              sx={{
                                fontSize: 20,
                                transform: "rotate(-45deg)",
                              }}
                            />
                          </IconButton>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>
                            {formatNumber(counsellor.sessions_completed)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>{formatNumber(retainers)}</Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>{formatCurrency(amountGenerated)}</Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            {onViewItem ? (
                              <IconButton
                                type="button"
                                aria-label={`View ${counsellorName} work`}
                                disabled={!counsellor.counsellor_id}
                                onClick={() => onViewItem(counsellor)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "7px",
                                  color: "#3568D4",
                                  "&:hover": { bgcolor: "#EEF4FF" },
                                }}
                              >
                                <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            ) : null}

                            <IconButton
                              type="button"
                              aria-label={`Edit ${counsellorName}`}
                              disabled={
                                !counsellor.counsellor_id ||
                                editLoadingId === counsellor.counsellor_id ||
                                Boolean(deletingId)
                              }
                              onClick={() => handleOpenEditCounsellor(counsellor)}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "7px",
                                color: NAVY,

                                "&:hover": {
                                  bgcolor: "rgba(41, 46, 92, 0.08)",
                                },
                              }}
                            >
                              {editLoadingId === counsellor.counsellor_id ? (
                                <CircularProgress size={16} sx={{ color: NAVY }} />
                              ) : (
                                <EditIcon sx={{ fontSize: 19 }} />
                              )}
                            </IconButton>

                            <IconButton
                              type="button"
                              aria-label={`Delete ${counsellorName}`}
                              disabled={
                                !counsellor.counsellor_id ||
                                deletingId === counsellor.counsellor_id ||
                                editLoadingId === counsellor.counsellor_id
                              }
                              onClick={() =>
                                setDeleteTarget({
                                  id: counsellor.counsellor_id,
                                  name: counsellorName,
                                })
                              }
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "7px",
                                color: "#D92D20",

                                "&:hover": {
                                  bgcolor: "#FEF3F2",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              minHeight: 36,
            }}
          >
            <IconButton
              aria-label="Previous page"
              disabled={page <= 1 || counsellorsLoading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              sx={{
                width: 28,
                height: 28,
                color: "#6E6893",
                borderRadius: "7px",
              }}
            >
              <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {paginationItems.map((item) => {
              if (typeof item === "string") {
                return (
                  <Typography
                    key={item}
                    sx={{
                      color: "#292948",
                      fontSize: 14,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      lineHeight: "26px",
                    }}
                  >
                    ..........
                  </Typography>
                );
              }

              const isActive = item === page;

              return (
                <Button
                  key={item}
                  disabled={counsellorsLoading}
                  onClick={() => setPage(item)}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: "999px",
                    color: isActive ? "#F1EEF9" : "#292948",
                    bgcolor: isActive ? NAVY : "transparent",
                    fontSize: 14,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    lineHeight: "26px",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: isActive ? NAVY : "rgba(41, 46, 92, 0.06)",
                    },
                  }}
                >
                  {item}
                </Button>
              );
            })}

            <IconButton
              aria-label="Next page"
              disabled={page >= totalPages || counsellorsLoading}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              sx={{
                width: 28,
                height: 28,
                color: "#6E6893",
                borderRadius: "7px",
              }}
            >
              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Typography
              sx={{
                color: "#292948",
                fontSize: 12,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                letterSpacing: 0.6,
                ml: 0.5,
              }}
            >
              Total - {formatNumber(totalRecords)}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <AddCounsellorModal
        open={addModalOpen}
        submitting={creating}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleCreateCounsellor}
        label={label}
      />

      <AddCounsellorModal
        open={editModalOpen}
        mode="edit"
        initialValues={editCounsellor}
        submitting={updating}
        onClose={handleCloseEditCounsellor}
        onSubmit={handleUpdateCounsellor}
        label={label}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deletingId) {
            setDeleteTarget(null);
          }
        }}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: "10px",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#171725",
            fontSize: 18,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
          }}
        >
          Delete {label.toLowerCase()}
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: "#667085", fontSize: 14, lineHeight: "22px" }}>
            This will remove {deleteTarget?.name || `this ${label.toLowerCase()}`} from the
            admin {label}s list.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            disabled={Boolean(deletingId)}
            onClick={() => setDeleteTarget(null)}
            sx={{
              borderColor: "#D0D5DD",
              color: "#344054",
              textTransform: "none",
              borderRadius: "7px",
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="contained"
            disabled={Boolean(deletingId)}
            onClick={handleDeleteCounsellor}
            sx={{
              bgcolor: "#D92D20",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: "7px",
              boxShadow: "none",

              "&:hover": {
                bgcolor: "#B42318",
                boxShadow: "none",
              },
            }}
          >
            {deletingId ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </LayoutComponent>
  );
};

CounsellorsPage.propTypes = {
  fetchStats: PropTypes.func,
  fetchItems: PropTypes.func,
  createItem: PropTypes.func,
  fetchItem: PropTypes.func,
  updateItem: PropTypes.func,
  deleteItem: PropTypes.func,
  onViewItem: PropTypes.func,
  layoutComponent: PropTypes.elementType,
  label: PropTypes.string,
};

export default CounsellorsPage;
